// src/redux/sagas/imageUploadSaga.ts
import { call, put, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { request } from '../../api/request';
import { IMAGE_UPLOAD_URL } from '../../api/config';
import {
  uploadImageRequest,
  uploadImageSuccess,
  uploadImageFailure,
} from '../slices/imageUploadSlice';
import { shouldSkipApiCalls } from '../../utils/appConfig';

// Interface for upload response
interface ImageUploadResponse {
  image_url: string;
}

function* uploadImageSaga(action: PayloadAction<File>): Generator<
  unknown,
  void,
  AxiosResponse<ImageUploadResponse>
> {
  // Skip API calls if in preview mode
  if (shouldSkipApiCalls()) {
    return;
  }

  try {
    const file = action.payload;

    // Validate file
    if (!file) {
      yield put(uploadImageFailure('No file provided'));
      return;
    }

    // Validate file type (optional - add more types as needed)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      yield put(uploadImageFailure('Invalid file type. Please upload an image file.'));
      return;
    }


    // Create FormData
    const formData = new FormData();
    formData.append('image', file);

    // Make the upload request
    const response = yield call(request<ImageUploadResponse>, {
      method: 'POST',
      url: '/imageUpload', // This will be appended to IMAGE_UPLOAD_URL
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      base: IMAGE_UPLOAD_URL,
      requiresAuth: false,
      requiresRestaurantId: false,
    });

    // Extract image URL from response
    const uploadedImageUrl = response?.data?.image_url;

    if (!uploadedImageUrl) {
      yield put(uploadImageFailure('Upload successful but no image URL returned'));
      return;
    }

    yield put(uploadImageSuccess(uploadedImageUrl));

  } catch (error: unknown) {
    let errorMessage = 'Failed to upload image';
    
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'object' && error !== null) {
      const axiosError = error as any;
      if (axiosError.response?.data?.message) {
        errorMessage = axiosError.response.data.message;
      } else if (axiosError.response?.data?.error) {
        errorMessage = axiosError.response.data.error;
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }
    }

    yield put(uploadImageFailure(errorMessage));
  }
}

// Watcher saga
export function* watchImageUploadSaga() {
  yield takeEvery(uploadImageRequest.type, uploadImageSaga);
}
